import type { IconName } from './icons';

/**
 * The panel menu: a few sections in the sidebar, each with tabs over its pages (see MENIU_PLANAS.md). The
 * pages keep their own addresses; a section only groups them. A tab with `soon` opens the coming-soon page
 * of a Shop add-on.
 */

export interface NavTab {
  to: string;
  label: string;
  /** Matched against the address too: one page can be several tabs (Atsiskaitymai's lists). */
  search?: Record<string, string>;
  hash?: string;
  soon?: boolean;
}

export interface NavSection {
  id: string;
  label: string;
  icon: IconName;
  tabs: NavTab[];
  /** Pages under the section without a tab of their own (an invoice form belongs to Sąskaitos). */
  also?: string[];
}

const soon = (id: string, label: string): NavTab => ({ to: '/soon', label, search: { f: id }, soon: true });

export const SECTIONS: NavSection[] = [
  { id: 'overview', label: 'Apžvalga', icon: 'grid', tabs: [
    { to: '/', label: 'Apžvalga' },
    { to: '/firm', label: 'Buhalterio panelė' },
    { to: '/assistant', label: 'AI asistentas' },
  ] },
  { id: 'invoices', label: 'Sąskaitos', icon: 'file', also: ['/outgoing', '/incoming'], tabs: [
    { to: '/invoices', label: 'Sąskaitos' },
    { to: '/quotes', label: 'Pasiūlymai' },
    { to: '/recurring', label: 'Periodinės' },
    { to: '/templates', label: 'Šablonai' },
    { to: '/services', label: 'Paslaugos' },
  ] },
  { id: 'partners', label: 'Partneriai', icon: 'users', tabs: [
    { to: '/customers', label: 'Klientai' },
    { to: '/suppliers', label: 'Tiekėjai' },
    { to: '/reconciliation', label: 'Balansai', search: { tab: 'partners' } },
    { to: '/reconciliation', label: 'Skolų žiniaraštis', search: { tab: 'ledger' } },
    { to: '/reconciliation', label: 'Suderinimo aktai', search: { tab: 'acts' } },
    { to: '/agreements', label: 'Sutartys' },
  ] },
  { id: 'money', label: 'Pinigai', icon: 'wallet', tabs: [
    { to: '/bank', label: 'Bankas' },
    { to: '/cash', label: 'Kasa' },
    { to: '/reconciliation', label: 'Vidiniai pervedimai', search: { tab: 'transfers' } },
    { to: '/payments', label: 'Apmokėjimas internetu' },
    soon('bank-sync', 'Banko sinchronizacija'),
  ] },
  { id: 'accounting', label: 'Buhalterija', icon: 'percent', tabs: [
    { to: '/taxes', label: 'Mokesčiai' },
    { to: '/payroll', label: 'Atlyginimai' },
    { to: '/dividends', label: 'Dividendai' },
    { to: '/close', label: 'Uždarymas' },
    { to: '/accountant', label: 'Buhalteriui' },
    { to: '/journal', label: 'Žurnalas' },
    { to: '/books', label: 'Didžioji knyga' },
    { to: '/fixed-assets', label: 'Ilgalaikis turtas' },
    { to: '/accounts', label: 'Sąskaitų planas' },
    { to: '/export', label: 'Eksportas' },
    { to: '/declarations', label: 'Deklaracijos' },
    { to: '/iv', label: 'Individuali veikla' },
  ] },
  { id: 'work', label: 'Darbai', icon: 'check', tabs: [
    { to: '/tasks', label: 'Užduotys' },
    { to: '/calendar', label: 'Kalendorius' },
    { to: '/notes', label: 'Užrašai' },
    { to: '/files', label: 'Failai' },
  ] },
];

/** At the bottom of the sidebar. */
export const COMPANY_SECTION: NavSection = { id: 'company', label: 'Įmonė', icon: 'building', tabs: [
  { to: '/company', label: 'Rekvizitai' },
  { to: '/projects', label: 'Projektai' },
  { to: '/account', label: 'Vartotojai', hash: 'team' },
  { to: '/account', label: 'Nustatymai' },
  soon('integrations', 'Integracijos'),
  { to: '/support', label: 'Pagalba' },
] };

/** Plans and add-ons: on its own above Įmonė, where people find what they can switch on or buy. */
export const SHOP_SECTION: NavSection = { id: 'shop', label: 'Parduotuvė', icon: 'shop', tabs: [{ to: '/shop', label: 'Parduotuvė' }] };

export const ALL_SECTIONS = [...SECTIONS, SHOP_SECTION, COMPANY_SECTION];

/** Pages only for a person working on their own (Individuali veikla); a company does not see them. */
export const IV_ONLY = ['/iv'];
/** The accounting firm's board: only in a company on the Firm plan. */
export const FIRM_ONLY = ['/firm'];

/** The section's tabs without the pages the company switched off in the Shop. */
export const visibleTabs = (section: NavSection, disabled: string[] = []) => section.tabs.filter((tab) => !disabled.includes(tab.to));

const under = (path: string, to: string) => path === to || (to !== '/' && path.startsWith(`${to}/`));

/**
 * The section and tab of an address. A tab's search and hash must match when it has them; of several tabs
 * on one page the one that matches most wins, and without a match the page's first tab (its default view).
 */
export function locate(path: string, search: Record<string, unknown>, hash: string): { section: NavSection; tab: NavTab | null } | null {
  let best: { section: NavSection; tab: NavTab; score: number } | null = null;
  for (const section of ALL_SECTIONS) {
    for (const tab of section.tabs) {
      if (!under(path, tab.to)) continue;
      const keys = Object.entries(tab.search ?? {});
      if (keys.some(([k, v]) => search[k] !== undefined && String(search[k]) !== v)) continue;
      if (tab.hash && tab.hash !== hash) continue;
      if (tab.search?.f && search.f !== tab.search.f) continue;
      const score = (path === tab.to ? 10 : 0) + keys.filter(([k]) => search[k] !== undefined).length * 2 + (tab.hash ? 3 : 0);
      if (!best || score > best.score) best = { section, tab, score };
    }
  }
  if (best) return { section: best.section, tab: best.tab };
  const section = ALL_SECTIONS.find((s) => s.also?.some((p) => under(path, p)));
  return section ? { section, tab: null } : null;
}
